import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";

import { api, type GroupSummary } from "../../lib/api";
import { formatSigned, toneClass } from "../../lib/money";
import { cn } from "../../lib/utils";

export function useGroups() {
  return useQuery({
    queryKey: ["groups"],
    queryFn: () => api.get<GroupSummary[]>("/groups"),
  });
}

export function GroupSwitcher({ currentGroupId }: { currentGroupId: number }) {
  const navigate = useNavigate();
  const { data } = useGroups();
  const current = data?.find((group) => group.id === currentGroupId);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          // Same height, border and corner as the home button beside it, so the two read
          // as one control group rather than two unrelated boxes.
          className="tap flex items-center gap-2 rounded-lg border border-rule bg-raised px-3 text-sm transition-colors hover:border-ink/25"
        >
          <span className="max-w-[9rem] truncate font-medium">{current?.name ?? "Group"}</span>
          <span aria-hidden className="text-muted">
            ▾
          </span>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          sideOffset={6}
          align="start"
          className="z-50 min-w-[16rem] rounded border border-rule bg-raised p-1 shadow-sm"
        >
          {(data ?? []).map((group) => (
            <DropdownMenu.Item
              key={group.id}
              onSelect={() => navigate(`/g/${group.id}`)}
              className={cn(
                "flex cursor-pointer items-center justify-between gap-4 rounded px-2 py-2 text-sm outline-none",
                "data-[highlighted]:bg-ink/5",
                group.id === currentGroupId && "font-medium",
              )}
            >
              <span className="truncate">{group.name}</span>
              <span className={cn("num text-xs", toneClass(group.my_balance_base_cents))}>
                {formatSigned(group.my_balance_base_cents, group.base_currency)}
              </span>
            </DropdownMenu.Item>
          ))}
          <DropdownMenu.Separator className="my-1 h-px bg-rule" />
          <DropdownMenu.Item asChild>
            <Link
              to="/"
              className="flex cursor-pointer rounded px-2 py-2 text-sm outline-none data-[highlighted]:bg-ink/5"
            >
              All groups
            </Link>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
